import React from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/ui";
import Seo from "@/components/Seo";
import { ImmersiveShell } from "@/components/immersive/ImmersiveShell";
import { Reveal } from "@/components/immersive/Reveal";

/** Shared immersive marketing shell. Same props API as before. */
export function MarketingPage({ eyebrow, title, blurb, primaryCta, features, testID, seo }) {
  const nav = useNavigate();
  return (
    <ImmersiveShell>
      {seo ? <Seo {...seo} /> : null}
      <section className="imm-mkt-hero imm-wrap" data-testid={testID}>
        <Reveal className="imm-eyebrow" delay={100}>{eyebrow}</Reveal>
        <Reveal as="h1" delay={280}>{title}</Reveal>
        <Reveal as="p" delay={540}>{blurb}</Reveal>
        <Reveal delay={800}>
          <div className="imm-mkt-actions">
            <button className="imm-cta" data-testid="mkt-primary" onClick={() => nav(primaryCta.route)}>
              <Icon name={primaryCta.icon} size={18} /> {primaryCta.label}
            </button>
            <button className="imm-cta ghost" data-testid="mkt-signin" onClick={() => nav("/login")}>Sign in</button>
          </div>
        </Reveal>
      </section>

      <section className="imm-wrap" style={{ paddingBottom: "10vh" }}>
        <Reveal className="imm-eyebrow" style={{ marginBottom: 6 }}>What you get</Reveal>
        {features.map((f, i) => (
          <Reveal key={i} delay={i * 75}>
            <div className="imm-feature">
              <span className="fnum">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal delay={100}>
          <div style={{ marginTop: 56 }}>
            <button className="imm-cta" data-testid="mkt-bottom-cta" onClick={() => nav(primaryCta.route)}>
              <Icon name={primaryCta.icon} size={18} /> {primaryCta.label}
            </button>
          </div>
        </Reveal>
      </section>
    </ImmersiveShell>
  );
}

export default function ForFamilies() {
  return (
    <MarketingPage
      testID="for-families"
      seo={{ title: "In-home care for your family", path: "/for-families", description: "Describe your loved one's needs and start date — HomecareMatches matches you with a vetted home care agency and caregiver, with a free day of care after your first 5 days and a refundable deposit." }}
      eyebrow="For families"
      title="The right caregiver, matched by people — not an algorithm."
      blurb="Describe your loved one's needs and when you need to start. Our team matches you with a vetted partner agency and caregiver, runs a non-clinical nurse video call, and gives you a free day of care after your first 5 days."
      primaryCta={{ label: "Find care now", icon: "home", route: "/intake" }}
      features={[
        { icon: "gift", title: "A free day of care", desc: "After your first 5 days of service, get 1 free day (up to 8 hours), unbilled. No commitment." },
        { icon: "shield", title: "Vetted agencies only", desc: "We verify license, insurance, background-check standard and complaint history before any agency joins the pool." },
        { icon: "user-check", title: "A nurse video call", desc: "A HomecareMatches RN builds a portable, non-clinical care preferences record so you never repeat yourself." },
        { icon: "credit-card", title: "$200 refundable deposit", desc: "Reserves your trial hours. Cancel 24h+ ahead and keep everything — platform reassignments never forfeit." },
        { icon: "alert-circle", title: "A 5-day concern promise", desc: "Raise a concern any time. If it isn't resolved in 5 business days we reassign you to a new partner agency." },
        { icon: "heart", title: "Referrals reward you", desc: "Refer a family — they get 8 free hours, you earn 4 more added to your care." },
      ]}
    />
  );
}
